import React, { forwardRef, type ReactElement, type ReactNode } from 'react';

import classnames from 'classnames';

import { type FloatingPlacement, type FloatingProps } from '../Floating/Floating.types';
import { FloatingArrow } from '../Floating/FloatingArrow';
import { FloatingContainer } from '../Floating/FloatingContainer';
import { FloatingContent } from '../Floating/FloatingContent';
import { FloatingPortal } from '../Floating/FloatingPortal';
import { FloatingTrigger } from '../Floating/FloatingTrigger';
import { useFloatingTriggerProps } from '../Floating/useFloatingTriggerProps';
import { Text } from '../Typography';

import styles from './Popover.module.scss';

export type PopoverProps = Omit<FloatingProps, 'content'> & {
    /**
     * Children elements inside a component
     */
    children: ReactNode;
    /**
     * Content of the card
     */
    content: ReactNode | (() => ReactNode);
    /**
     * The additional css class applied to the popover content
     */
    className?: string;
    /**
     * Whether the floating card is open or not
     */
    open?: boolean;
    /**
     * Callback executed when visibility of the tooltip card is changed
     */
    onOpenChange?: (open: boolean) => void;
    /**
     * Specifies the placement of the popover
     */
    placement?: FloatingPlacement;
    /**
     * The title of the card
     */
    title?: ReactNode;
    /**
     * Whether the popover is hidden or not
     */
    hidden?: boolean;
    /**
     * Removes the default padding from the popover content
     */
    noPadding?: boolean;
    style?: React.CSSProperties;
    /**
     * Whether the popover has no elevation (box shadow)
     */
    noElevation?: boolean;
};

export const Popover = forwardRef<HTMLDivElement, PopoverProps>(
    (
        {
            children,
            className,
            content,
            open,
            onOpenChange,
            placement,
            title,
            trigger = ['hover', 'focus'],
            noArrow,
            destroyOnHide = false,
            hidden,
            noPadding,
            mouseEnterDelay,
            mouseLeaveDelay,
            animationDuration,
            mouseRestDelay,
            align,
            getPopupContainer,
            noElevation,
            popupRef,
            role = 'tooltip',
            style,
            keepTriggerFocused,
            stickyOnTopWhileScrolling,
            ...rest
        }: PopoverProps,
        ref
    ): ReactElement => {
        const [triggerProps, contentProps] = useFloatingTriggerProps(rest);
        const resolvedContent = typeof content === 'function' ? content() : content;
        const isResolvedContentText = typeof resolvedContent === 'string';
        const mergedHidden = Boolean(hidden || (!title && !resolvedContent));
        return (
            <FloatingContainer
                placement={placement}
                open={open}
                onOpenChange={onOpenChange}
                trigger={trigger}
                mouseEnterDelay={mouseEnterDelay}
                mouseLeaveDelay={mouseLeaveDelay}
                align={align}
                mouseRestDelay={mouseRestDelay}
                animationDuration={animationDuration}
                role={role}
                stickyOnTopWhileScrolling={stickyOnTopWhileScrolling}
            >
                <FloatingTrigger ref={ref} {...triggerProps}>
                    {children}
                </FloatingTrigger>
                {!mergedHidden && (
                    <FloatingPortal
                        getPopupContainer={getPopupContainer}
                        destroyOnHide={destroyOnHide}
                        ref={popupRef}
                        className={styles.portal}
                        style={style}
                        {...contentProps}
                    >
                        <FloatingContent
                            className={classnames(styles.content, className, {
                                [styles.noPadding]: noPadding,
                                [styles.noElevation]: noElevation
                            })}
                            shouldTrapFocus={keepTriggerFocused}
                        >
                            {title && (
                                <Text type="h5" compact>
                                    {title}
                                </Text>
                            )}
                            {resolvedContent && isResolvedContentText && (
                                <Text compact>{resolvedContent}</Text>
                            )}
                            {resolvedContent && !isResolvedContentText && resolvedContent}
                            {!noArrow && (
                                <FloatingArrow
                                    className={classnames(styles.arrow, {
                                        [styles.noElevation]: noElevation
                                    })}
                                />
                            )}
                        </FloatingContent>
                    </FloatingPortal>
                )}
            </FloatingContainer>
        );
    }
);

Popover.displayName = 'Popover';
